import { useId } from 'react';

import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import Stack from '@zenbusiness/zui/Stack';

import {
  ConstructorCalloutBar,
  ConstructorCalloutButton as Button,
  ConstructorCalloutCard as Card,
  ConstructorCalloutContentContainer as ContentContainer,
  ConstructorCalloutDescription as Description,
  ConstructorCalloutContent as Content,
  ConstructorCalloutTitle as Title
} from './ConstructorCallout.style';

const MAP_BACKGROUND_TO_CARD_COLOR = {
  light: 'paper',
  main: 'default'
};

const MAP_TYPE_TO_COLOR = {
  info: 'blue',
  success: 'green',
  warning: 'orange',
  error: 'red',
  ai: 'purple'
};

const MAP_VARIANT_TO_CARD_VARIANT = {
  contained: 'elevation',
  outlined: 'outlined'
};

export default function ConstructorCallout({
  background = 'main',
  description,
  primaryAction,
  title,
  type = 'info',
  variant = 'contained',
  ...delegated
}) {
  const uniqueId = useId();
  const calloutId = delegated.id ?? uniqueId;
  const labelledby = title ? `${calloutId}-callout-title` : `${calloutId}-callout-description`;
  const describedby = `${calloutId}-callout-description`;

  const barColor = MAP_TYPE_TO_COLOR[type] ?? MAP_TYPE_TO_COLOR.info;
  const cardColor = MAP_BACKGROUND_TO_CARD_COLOR[background] ?? MAP_BACKGROUND_TO_CARD_COLOR.main;
  const cardVariant = MAP_VARIANT_TO_CARD_VARIANT[variant] ?? MAP_VARIANT_TO_CARD_VARIANT.contained;

  const { label: primaryActionLabel, ...primaryActionProps } = primaryAction ?? {};

  return (
    <Card
      aria-describedby={describedby}
      aria-labelledby={labelledby}
      color={cardColor}
      role="note"
      variant={cardVariant}
      {...delegated}
    >
      <ContentContainer>
        <ConstructorCalloutBar color={barColor} data-testid="callout-bar" />

        <Stack gap={2}>
          <Content>
            {title && <Title id={labelledby}>{title}</Title>}
            {description && <Description id={describedby}>{description}</Description>}
          </Content>

          {primaryAction && (
            <Box>
              <Button
                clickstreamProps={{
                  element_name: 'constructor_callout_primary_action'
                }}
                {...primaryActionProps}
              >
                {primaryActionLabel}
              </Button>
            </Box>
          )}
        </Stack>
      </ContentContainer>
    </Card>
  );
}

ConstructorCallout.propTypes = {
  background: PropTypes.oneOf(['light', 'main']),
  description: PropTypes.node.isRequired,
  primaryAction: PropTypes.shape({
    label: PropTypes.node.isRequired,
    ...Button.propTypes
  }),
  title: PropTypes.node,
  type: PropTypes.oneOf(['info', 'success', 'warning', 'error', 'ai']),
  variant: PropTypes.oneOf(['contained', 'outlined'])
};
